"use client";

import {
  ESports,
  ImxRewardsMUI,
  People,
  Shield,
  SupportChat,
} from "@/components/icons";
import { Box, Divider, Logo, Stack } from "@biom3/react";
import styled from "@emotion/styled";
import { usePathname, useRouter } from "next/navigation";
import type { FC, ReactElement, ReactNode, Ref } from "react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDevRev } from "@/components/DevRevProvider";
import { ProfileDrawer } from "@/components/ProfileDrawer/ProfileDrawer";
import { PlayLogo } from "@/components/icons/PlayLogo";
import {
  APP_HEADER_HEIGHT,
  GAMES_PAGE,
  QUEST_PAGE,
  REFERRAL_PAGE,
  SIDEBAR_WIDTH,
} from "@/constants";
import {
  CheckoutProvider,
  NotificationProvider,
  usePassportProvider,
} from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useProfileDrawer } from "@/context/ProfileDrawerContext";
import { isMobile } from "@/utils/browser";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import { MinifiedMenuItem } from "./MinifiedMenuItem";
import { DesktopProfileCard } from "./ProfileCard";

type SideMenuSx = {
  translate?: {
    default: string;
    medium?: string;
  };
  opacity?: {
    default: string;
    medium?: string;
  };
  pointerEvents?: {
    default: string;
    medium?: string;
  };
};

const SideMenuContainer = styled.aside<{ sx?: SideMenuSx; topOffset?: string }>`
  width: ${SIDEBAR_WIDTH};
  height: 100%;
  position: fixed;
  z-index: 2;
  top: 0;
  left: 0;
  background: rgba(26, 26, 26, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(0px);
  border-right: 1px solid rgba(243, 243, 243, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  transition: transform 0.2s ease, opacity 0.2s ease;

  @media (width < 768px) {
    width: 100%;
    height: calc(100% - ${({ topOffset }) => topOffset || APP_HEADER_HEIGHT});
    top: ${({ topOffset }) => topOffset || APP_HEADER_HEIGHT};
    background: rgba(26, 26, 26, 0.9);
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    transform: ${({ sx }) => (sx?.translate?.default === "0 0" ? "translateX(0)" : "translateX(-100%)")};
    opacity: ${({ sx }) => (sx?.opacity?.default === "1" ? "1" : "0")};
    pointer-events: ${({ sx }) => (sx?.pointerEvents?.default === "auto" ? "auto" : "none")};
    padding: 16px;
    z-index: 5;
  }
`;

const InnerContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;

  @media (max-width < 768px) {
    max-width: ${SIDEBAR_WIDTH};
  }
`;

type MenuItemType = {
  visible: boolean;
  key: string;
  label: string;
  path?: string;
  divider?: boolean;
  onClick?: () => void;
  icon: ReactElement;
  bottom?: boolean;
  badge?: ReactNode;
};

interface MinifiedSideMenuProps {
  domRef: Ref<HTMLDivElement>;
  onItemClick: () => void;
  sx?: SideMenuSx;
  topOffset?: string;
}

function isMenuItemSelected(pathname: string, itemPath?: string) {
  if (!itemPath) return false;
  if (pathname === itemPath) return true;
  return itemPath !== "/" && pathname.includes(itemPath);
}

export const MinifiedSideMenu: FC<MinifiedSideMenuProps> = ({
  domRef,
  onItemClick,
  sx,
  topOffset,
}) => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { isLoggedIn } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const [isCopyWalletModalVisible, setCopyWalletModalVisible] = useState(false);
  const { setIsOpen: setProfileDrawerOpen } = useProfileDrawer();
  const router = useRouter();
  const isLessThanMediumScreen = useIsLessThanMediumScreen();
  const { openWidget } = useDevRev();

  const menuItems = useMemo(
    (): MenuItemType[] => [
      {
        visible: true,
        key: "quest_gems",
        label: "daily_gems",
        path: QUEST_PAGE,
        icon: <Shield variant="bold" />,
      },
      {
        visible: true,
        key: "games",
        label: "games",
        path: GAMES_PAGE,
        icon: <ESports variant="bold" />,
      },
      {
        visible: true,
        key: "rewards",
        label: "rewards",
        path: "/rewards",
        icon: <ImxRewardsMUI variant="bold" />,
      },
      {
        visible: true,
        key: "referral",
        label: "Refer friends",
        path: REFERRAL_PAGE,
        icon: <People variant="bold" />,
        bottom: true,
        badge: "300 Gems",
      },
      {
        visible: !isLessThanMediumScreen,
        key: "help",
        label: "Help",
        icon: <SupportChat variant="bold" />,
        bottom: true,
        onClick: openWidget,
      },
    ],
    [isLessThanMediumScreen, openWidget],
  );

  const onItem = () => {
    onItemClick();
  };

  const onProfileClick = () => {
    if (!isLoggedIn) {
      openLoginModal(async () => {
        if (isMobile()) router.replace("/quests");
      });
    } else {
      setProfileDrawerOpen(true);
    }
  };

  return (
    <SideMenuContainer ref={domRef} sx={sx} topOffset={topOffset}>
      <InnerContainer>
        {/* Desktop logo goes on top */}
        <Box
          sx={{
            display: { default: "none", medium: "flex" },
            justifyContent: "center",
            p: "base.spacing.x1",
            cursor: "pointer",
          }}
          aria-label="Home"
          onClick={() => router.push("/")}
        >
          <PlayLogo
            sx={{
              width: "100%",
              height: 48,
              color: "white",
            }}
          />
        </Box>
        <Stack gap="base.spacing.x4">
          <CheckoutProvider>
            <NotificationProvider>
              <DesktopProfileCard
                isLoggedIn={isLoggedIn}
                onProfileClick={onProfileClick}
              />
              <ProfileDrawer
                setCopyWalletModalVisible={setCopyWalletModalVisible}
                isCopyWalletModalVisible={isCopyWalletModalVisible}
                showTarget={false}
              />
            </NotificationProvider>
          </CheckoutProvider>
        </Stack>
        <Divider
          sx={{
            display: {
              default: "none",
              medium: "block",
            },
            height: "1px",
            background: "rgba(243, 243, 243, 0.1)",
            margin: "8px 0",
          }}
        />
        <Stack gap="base.spacing.x2" sx={{ flex: 1 }}>
          {menuItems
            .filter((item) => item.visible && !item.bottom)
            .map((item) => (
              <Fragment key={item.key}>
                <>
                  <MinifiedMenuItem
                    testId={`minified-menu-item__${item.key}`}
                    isSelected={isMenuItemSelected(pathname || "", item.path)}
                    path={item.path}
                    onClick={item.onClick || onItem}
                    icon={item.icon}
                    label={t(item.label)}
                    badge={item.badge}
                  />
                  {item.divider && (
                    <Divider
                      sx={{
                        display: "block",
                        height: "1px",
                        background: "rgba(243, 243, 243, 0.1)",
                        margin: "8px 0",
                      }}
                    />
                  )}
                </>
              </Fragment>
            ))}
        </Stack>

        {/* Bottom menu items */}
        <Box sx={{ mt: "auto" }}>
          {menuItems.some((item) => item.visible && item.bottom) && (
            <Divider
              sx={{
                display: "block",
                height: "1px",
                background: "rgba(243, 243, 243, 0.1)",
                margin: "8px 0",
              }}
            />
          )}
          <Stack gap="base.spacing.x2" sx={{ mt: "base.spacing.x2" }}>
            {menuItems
              .filter((item) => item.visible && item.bottom)
              .map((item) => (
                <Fragment key={item.key}>
                  <>
                    <MinifiedMenuItem
                      testId={`minified-menu-item__${item.key}`}
                      isSelected={isMenuItemSelected(pathname || "", item.path)}
                      path={item.path}
                      onClick={item.onClick || onItem}
                      icon={item.icon}
                      label={t(item.label)}
                      badge={item.badge}
                    />
                    {item.divider && (
                      <Divider
                        sx={{
                          display: "block",
                          height: "1px",
                          background: "rgba(243, 243, 243, 0.1)",
                          margin: "8px 0",
                        }}
                      />
                    )}
                  </>
                </Fragment>
              ))}
          </Stack>
        </Box>

        {/* Mobile logo goes on bottom */}
        <Box
          sx={{
            mt: "auto",
            pb: "base.spacing.x1",
            display: { default: "flex", medium: "none" },
            flexDirection: "column",
          }}
        >
          <Divider
            sx={{
              display: "block",
              height: "1px",
              background: "rgba(243, 243, 243, 0.1)",
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "base.spacing.x3",
              px: "base.spacing.x3",
            }}
          >
            <Logo
              logo="PlayHorizontalLockupCompact"
              sx={{
                padding: "base.spacing.x2",
                width: "100%",
                maxWidth: "120px",
                color: "base.color.neutral.600",
              }}
            />
          </Box>
        </Box>
      </InnerContainer>
    </SideMenuContainer>
  );
};
